import Shepherd from 'shepherd.js';
import type { Step } from 'shepherd.js';
import { useAuthStore } from '../stores/auth';

interface TourStep {
  id: string;
  title: string;
  text: string;
  attachTo?: { element: string; on: string };
  buttons?: Array<{
    text: string;
    type?: 'back' | 'next' | 'cancel';
    classes?: string;
    action?: () => void;
  }>;
  beforeShowPromise?: () => Promise<void>;
}

interface TourOptions {
  tourId: string;
  steps: TourStep[];
  useModalOverlay?: boolean;
  delayMs?: number;
  onComplete?: () => void;
  onCancel?: () => void;
}

function getStorageKey(userId: string): string {
  return `ha_tours_${userId}`;
}

function getCompletedTours(userId: string): string[] {
  try {
    const stored = localStorage.getItem(getStorageKey(userId));
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    return parsed.completedTours || [];
  } catch {
    return [];
  }
}

function markTourCompleted(userId: string, tourId: string): void {
  const completed = getCompletedTours(userId);
  if (!completed.includes(tourId)) {
    completed.push(tourId);
    localStorage.setItem(
      getStorageKey(userId),
      JSON.stringify({ completedTours: completed })
    );
  }
}

/**
 * Sync completed tours from server profile metadata into localStorage.
 * Called on auth init to ensure cross-device persistence.
 */
export function syncToursFromProfile(
  userId: string,
  metadata: Record<string, any> | undefined | null
): void {
  if (!metadata?.completed_tours || !Array.isArray(metadata.completed_tours)) {
    return;
  }

  const localCompleted = getCompletedTours(userId);
  const serverCompleted: string[] = metadata.completed_tours;

  // Merge server tours into local
  const merged = Array.from(new Set([...localCompleted, ...serverCompleted]));
  localStorage.setItem(
    getStorageKey(userId),
    JSON.stringify({ completedTours: merged })
  );
}

export function useTour(options: TourOptions) {
  const { tourId, steps, useModalOverlay = true, delayMs = 800, onComplete, onCancel } = options;

  let tour: Shepherd.Tour | null = null;

  function buildTour(): Shepherd.Tour {
    const authStore = useAuthStore();

    const shepherdTour = new Shepherd.Tour({
      useModalOverlay,
      defaultStepOptions: {
        classes: 'ha-shepherd-step',
        scrollTo: { behavior: 'smooth', block: 'center' },
        cancelIcon: { enabled: true },
      },
    });

    steps.forEach((step, index) => {
      const isFirst = index === 0;
      const isLast = index === steps.length - 1;

      // Build buttons if not custom-defined
      let buttons: Step.StepOptionsButton[] = [];
      if (step.buttons) {
        buttons = step.buttons.map((btn) => ({
          text: btn.text,
          classes: btn.classes || (btn.type === 'cancel'
            ? 'shepherd-button-secondary'
            : 'shepherd-button-primary'),
          action: btn.action || (() => {
            if (btn.type === 'back') shepherdTour.back();
            else if (btn.type === 'next') shepherdTour.next();
            else if (btn.type === 'cancel') shepherdTour.cancel();
          }),
        }));
      } else {
        if (!isFirst) {
          buttons.push({
            text: 'Back',
            classes: 'shepherd-button-secondary',
            action: () => shepherdTour.back(),
          });
        }
        buttons.push({
          text: isLast ? 'Done' : 'Next',
          classes: 'shepherd-button-primary',
          action: () => (isLast ? shepherdTour.complete() : shepherdTour.next()),
        });
      }

      const stepConfig: Step.StepOptions = {
        id: step.id,
        title: step.title,
        text: step.text,
        buttons,
      };

      if (step.attachTo) {
        stepConfig.attachTo = {
          element: step.attachTo.element,
          on: step.attachTo.on as any,
        };
      }

      if (step.beforeShowPromise) {
        stepConfig.beforeShowPromise = step.beforeShowPromise;
      }

      shepherdTour.addStep(stepConfig);
    });

    // On complete
    shepherdTour.on('complete', () => {
      if (authStore.user?.id) {
        markTourCompleted(authStore.user.id, tourId);
        syncToServer(authStore);
      }
      onComplete?.();
    });

    // On cancel
    shepherdTour.on('cancel', () => {
      if (authStore.user?.id) {
        markTourCompleted(authStore.user.id, tourId);
        syncToServer(authStore);
      }
      onCancel?.();
    });

    return shepherdTour;
  }

  async function syncToServer(authStore: ReturnType<typeof useAuthStore>): Promise<void> {
    if (!authStore.user?.id) return;
    const completed = getCompletedTours(authStore.user.id);
    try {
      // Merge with existing metadata to avoid overwriting other keys
      const existingMetadata = authStore.user.metadata || {};
      const mergedMetadata = { ...existingMetadata, completed_tours: completed };
      await authStore.updateProfile({ metadata: mergedMetadata } as any);
    } catch (err) {
      console.warn('[Tour] Failed to sync tour state to server:', err);
    }
  }

  function isCompleted(): boolean {
    const authStore = useAuthStore();
    if (!authStore.user?.id) return false;
    return getCompletedTours(authStore.user.id).includes(tourId);
  }

  function start(): void {
    if (tour) {
      tour.cancel();
    }
    tour = buildTour();
    tour.start();
  }

  function stop(): void {
    tour?.cancel();
  }

  function tryAutoStart(): void {
    if (isCompleted()) return;

    setTimeout(() => {
      // Re-check in case auth state changed during delay
      if (isCompleted()) return;
      start();
    }, delayMs);
  }

  function reset(): void {
    const authStore = useAuthStore();
    if (!authStore.user?.id) return;
    const completed = getCompletedTours(authStore.user.id);
    const filtered = completed.filter((id) => id !== tourId);
    localStorage.setItem(
      getStorageKey(authStore.user.id),
      JSON.stringify({ completedTours: filtered })
    );
  }

  return {
    start,
    stop,
    tryAutoStart,
    isCompleted,
    reset,
  };
}
